import React, { useMemo } from 'react';

import { useChildComponent } from '@gogame/hooks-ts';
import { fromTimeZoneToUTC, dateFNS } from '@gogame/intl-ts';

import MasterCampaignDetails from 'scenes/Influencers/MasterCampaigns/components/MasterCampaignDetails';

import {
  APPS_FLYER_PROVIDER,
  MY_TRACKER_PROVIDER,
} from 'scenes/Influencers/MasterCampaigns/constants';
import { useLoadMasterCampaign } from 'scenes/Influencers/MasterCampaigns/data';
import {
  useCreateMasterCampaign,
  useUpdateMasterCampaign,
} from 'scenes/Influencers/MasterCampaigns/data/mutations';
import MasterCampaignForm from './MasterCampaignForm';
import {
  NAME,
  GENERATE_NAME_AUTOMATICALLY,
  PERIOD,
  PAYOUT_RATIOS,
  ATTRIBUTION_PROVIDER_ID,
} from './constants';

const normalizeSubmitValues = ({
  [NAME]: name,
  [GENERATE_NAME_AUTOMATICALLY]: generateNameAutomatically,
  ...values
}) => ({
  ...values,
  [NAME]: generateNameAutomatically ? null : name,
});

export const MasterCampaignFormCreate = ({
  onSubmit,
  onClose,
  attributionProviderId,
  onExited,
  open,
}) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const [createMasterCampaign, { isLoading }] = useCreateMasterCampaign();
  const handleSubmit = async (values = {}) => {
    const createdMasterCampaign = await createMasterCampaign(
      normalizeSubmitValues(values),
      ({ id: existId }) => {
        openChildComponent(MasterCampaignDetails, { id: existId });
      }
    );

    onSubmit(createdMasterCampaign);
  };

  const subTitle = useMemo(() => {
    switch (attributionProviderId) {
      case APPS_FLYER_PROVIDER:
        return 'AppsFlyer';
      case MY_TRACKER_PROVIDER:
        return 'MyTracker';
    }
  }, [attributionProviderId]);

  return (
    <MasterCampaignForm
      title={'Add Master Campaign'}
      subTitle={subTitle}
      loading={isLoading}
      onClose={onClose}
      initialValues={{
        [ATTRIBUTION_PROVIDER_ID]: attributionProviderId,
        [GENERATE_NAME_AUTOMATICALLY]: true,
        [PERIOD]: fromTimeZoneToUTC(
          dateFNS.startOfMonth(dateFNS.addMonths(new Date(), 1))
        ),
        [PAYOUT_RATIOS]: {},
      }}
      onSubmit={handleSubmit}
      onExited={onExited}
      open={open}
    >
      {childComponent}
    </MasterCampaignForm>
  );
};

export const MasterCampaignFormEdit = ({
  id,
  onClose,
  onSubmit,
  onExited,
  open,
}) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const { data, isLoading } = useLoadMasterCampaign({ id });
  const [updateMasterCampaign, { isLoading: isUpdating }] =
    useUpdateMasterCampaign();

  const subTitle = useMemo(() => {
    switch (data?.attributionProviderId) {
      case APPS_FLYER_PROVIDER:
        return `AppsFlyer / ${data?.[NAME]}`;
      case MY_TRACKER_PROVIDER:
        return `MyTracker / ${data?.[NAME]}`;
      default:
        return `${data?.[NAME] || ''}`;
    }
  }, [data?.attributionProviderId]);

  const handleSubmit = async (values = {}) => {
    const response = await updateMasterCampaign(
      normalizeSubmitValues(values),
      ({ id: existId }) => {
        openChildComponent(MasterCampaignDetails, { id: existId });
      }
    );
    onSubmit(response);
  };

  return (
    <MasterCampaignForm
      title={'Edit Master Campaign'}
      subTitle={subTitle}
      entityId={id}
      loading={isUpdating || isLoading}
      onClose={onClose}
      initialValues={{
        ...data,
        [ATTRIBUTION_PROVIDER_ID]: data?.[ATTRIBUTION_PROVIDER_ID],
      }}
      active={!!data}
      onSubmit={handleSubmit}
      onExited={onExited}
      open={open}
    >
      {childComponent}
    </MasterCampaignForm>
  );
};

export const MasterCampaignFormDuplicate = ({
  id,
  onSubmit,
  onClose,
  onExited,
  open,
}) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const { data, isLoading } = useLoadMasterCampaign({ id });
  const [createMasterCampaign, { isLoading: isCreating }] =
    useCreateMasterCampaign();

  const subTitle = useMemo(() => {
    switch (data?.attributionProviderId) {
      case APPS_FLYER_PROVIDER:
        return 'AppsFlyer';
      case MY_TRACKER_PROVIDER:
        return 'MyTracker';
    }
  }, [data?.attributionProviderId]);

  const handleSubmit = async (values = {}) => {
    const createdMasterCampaign = await createMasterCampaign(
      normalizeSubmitValues(values),
      ({ id: existId }) => {
        openChildComponent(MasterCampaignDetails, { id: existId });
      }
    );

    onSubmit(createdMasterCampaign);
  };

  return (
    <MasterCampaignForm
      title={'Add Master Campaign'}
      subTitle={subTitle}
      loading={isLoading || isCreating}
      onClose={onClose}
      initialValues={{
        ...data,
        [ATTRIBUTION_PROVIDER_ID]: data?.[ATTRIBUTION_PROVIDER_ID],
        [GENERATE_NAME_AUTOMATICALLY]: true,
      }}
      onSubmit={handleSubmit}
      onExited={onExited}
      open={open}
    >
      {childComponent}
    </MasterCampaignForm>
  );
};
